import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardPanel } from "@/components/ui/card";
import { NAMESPACES } from "@/i18n/constants";
import type { CLOCK_CADENCE } from "@/providers/wall-clock";
import type { DashboardSpec } from "./ChartSpec";
import { DataViewerBody } from "./data-viewer-body";
import { DataViewerProvider } from "./data-viewer-provider";
import type { PanelLayoutToggleProps } from "./spec/panel-layout-toggle";
import { SpecProvider } from "./spec/spec-context";

function DataViewerMessage({ message }: { message: string }) {
    return (
        <Card>
            <CardPanel className={"text-muted-foreground text-sm text-center"}>{message}</CardPanel>
        </Card>
    );
}

export function DataViewer({
    initialSpec,
    isLoading,
    isError,
    isEmpty,
    autoRefreshCadence,
    resizable = true,
    controls,
    panelLayout,
    showCsvDownload,
    baseHeightPx,
}: {
    initialSpec: DashboardSpec;
    isLoading?: boolean;
    isError?: boolean;
    isEmpty?: boolean;
    autoRefreshCadence?: CLOCK_CADENCE;
    /** When false, panels fill available width at default height with no drag grips. */
    resizable?: boolean;
    /**
     * Rendered at the start of the toolbar's right-hand group, inside the providers, and never moved
     * into the overflow menu. The expanded dialog has its own toolbar, so this renders twice at
     * once: keep its state in the card, as `panelLayout` does, or in the viewer store, as the CSV
     * download does.
     */
    controls?: ReactNode;
    /** Adds the single/stacked toggle. */
    panelLayout?: PanelLayoutToggleProps;
    showCsvDownload?: boolean;
    /** The base height of each panel, in pixels */
    baseHeightPx?: number;
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "messages" });

    if (isLoading) {
        return <DataViewerMessage message={t("loading")} />;
    }
    if (isError) {
        return <DataViewerMessage message={t("load-failed")} />;
    }
    // A toolbar with no controls has nothing to offer an empty viewer, so the message takes the card.
    if (isEmpty && !controls) {
        return <DataViewerMessage message={t("empty")} />;
    }

    return (
        <SpecProvider initialSpec={initialSpec}>
            <DataViewerProvider defaultTimeRange={initialSpec.defaultTimeRange}>
                <DataViewerBody
                    autoRefreshCadence={autoRefreshCadence}
                    baseHeightPx={baseHeightPx}
                    controls={controls}
                    isEmpty={isEmpty}
                    panelLayout={panelLayout}
                    showCsvDownload={showCsvDownload}
                    resizable={resizable}
                />
            </DataViewerProvider>
        </SpecProvider>
    );
}
